"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Card, CardContent } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Calendar, Search, ArrowRight, BookOpen } from "lucide-react"

export default function BlogsPage() {
  const [blogs, setBlogs] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)

  // Only UI categories — but backend only returns "Blogs"
  const categories = ["all", "Blogs"]

  // *******************************
  // Fetch Blogs from WordPress API
  // *******************************
  const fetchBlogs = async (pageNumber = 1) => {
    try {
      setLoading(true)

      const res = await fetch(
        `/wp-json/wp/v2/blogs?per_page=20&page=${pageNumber}&_embed`
      )

      const data = await res.json()

      if (data.length < 20) setHasMore(false)

      const mappedBlogs = data.map((item) => {
        const featuredImage =
          item._embedded?.["wp:featuredmedia"]?.[0]?.source_url || null

        return {
          id: item.id,
          title: item.title.rendered,
          slug: item.slug,
          date: new Date(item.date).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
          }),
          category: "Blogs",
          excerpt:
            item.excerpt?.rendered?.replace(/<[^>]+>/g, "") ||
            item.acf?.hero_description?.slice(0, 120) ||
            "Read more about this article.",
          featuredImage,
        }
      })

      setBlogs((prev) => {
        const unique = [
          ...new Map(
            [...prev, ...mappedBlogs].map((item) => [item.id, item])
          ).values(),
        ]
        return unique
      })

      setLoading(false)
    } catch (error) {
      console.error("Error fetching blogs:", error)
      setLoading(false)
    }
  }

  // Load first 20 blogs
  useEffect(() => {
    fetchBlogs(1)
  }, [])

  // Search + Category Filter
  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch =
      blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesCategory =
      selectedCategory === "all" || selectedCategory === blog.category

    return matchesSearch && matchesCategory
  })

  return (
    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary/10 to-primary/5 py-16 md:py-20">
          <div className="container px-4 mx-auto text-center">
            <Badge className="mb-4 px-3 py-1">Health & Wellness</Badge>
            <h1 className="mb-6 text-4xl font-bold md:text-5xl lg:text-6xl">
              Pranaam <span className="text-primary">Health Blog</span>
            </h1>
            <p className="mb-8 text-lg text-muted-foreground max-w-3xl mx-auto">
              Expert health advice, medical insights, and wellness tips.
            </p>
          </div>
        </section>

        {/* Search + Filter */}
        <section className="py-12 bg-gray-100">
          <div className="container px-4 mx-auto max-w-6xl space-y-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 bg-white"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  onClick={() => setSelectedCategory(category)}
                  className="rounded-full"
                >
                  {category === "all" ? "All Topics" : category}
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Blogs Grid */}
        <section className="py-12 bg-white">
          <div className="container px-4 mx-auto max-w-6xl">
            {filteredBlogs.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredBlogs.map((blog) => (
                    <Card key={blog.id} className="group cursor-pointer hover:shadow-xl border-gray-200">
                      <CardContent className="p-0">
                        <div className="relative h-48 bg-gradient-to-br from-primary/10 to-gray-100">
                          {blog.featuredImage ? (
                            <img
                              src={blog.featuredImage}
                              alt={blog.title}
                              className="absolute inset-0 w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <BookOpen className="w-16 h-16 text-primary/20" />
                            </div>
                          )}
                          <div className="absolute top-4 right-4">
                            <Badge className="bg-white text-primary border-primary">{blog.category}</Badge>
                          </div>
                        </div>

                        <div className="p-6">
                          <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span>{blog.date}</span>
                          </div>

                          <h3 className="font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary">
                            {blog.title}
                          </h3>

                          <p className="text-gray-600 text-sm mb-4 line-clamp-3">{blog.excerpt}</p>

                          <Link
                            to={`/blogs/${blog.slug}`}
                            className="inline-flex items-center text-primary font-medium hover:underline"
                          >
                            Read More
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div className="text-center mt-12">
                    <Button
                      size="lg"
                      variant="outline"
                      disabled={loading}
                      onClick={() => {
                        setPage(page + 1)
                        fetchBlogs(page + 1)
                      }}
                      className="rounded-full border-primary text-primary"
                    >
                      {loading ? "Loading..." : "Load More Articles"}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No articles found</h3>
                <Button variant="outline" onClick={() => (setSearchQuery(""), setSelectedCategory("all"))}>
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  )
}
